import { useEffect, useState } from 'preact/hooks';
import { services } from '../app';
import { type HijriMonth, hijriName } from '../astro/hijri';
import { keepHonorifics } from '../data/islam';
import { EVIDENCE } from '../data/islamEvidence';
import { crescentKey, nights, sawCrescent } from '../live/nights';
import { crescentMonthAt, eveningOf29, saudiStarts } from '../live/saudi';
import { crescentFrame, panel, place, previewFrom, reliving, selection, settings, timeLive } from '../state';
import { Icon } from './icons';
import { SayArabic } from './SayArabic';

// The new crescent, Saudi Arabia's way (BUILD-SPEC items 19, 40 and 41): on the evening of the 29th, where to look and
// a frame on the horizon in pointing mode, with no prediction of whether it can be seen; "I saw the crescent" in Your
// nights; and the supplication on seeing it.

const HOUR = 3600e3;
/** Home: the family's own place, never one being previewed. */
const home = () => previewFrom.peek()?.home ?? place.peek();
/** The real sky here and now (not another time or place, nor a night being relived). */
const live = () => timeLive.value && !previewFrom.value && !reliving.value;

/** The supplication on seeing the new crescent, H-27 (Al-Tirmidhī 3451), as approved: its Arabic with Arabic on. */
export function CrescentPrayer() {
  const e = EVIDENCE['H-27'];
  return (
    <aside class="reminder cal-prayer crescent-prayer" aria-label="The supplication on seeing the new crescent">
      <div class="reminder-head"><Icon name="moon" size={15} /> The supplication on seeing the new crescent</div>
      <p>{keepHonorifics(e.lead)}</p>
      {settings.value.arabic && <p class="islam-ar" lang="ar" dir="rtl">{e.arabic}</p>}
      {settings.value.arabic && <SayArabic text={e.arabic} />}
      <p>{e.english}</p>
      <p class="cal-ref">{keepHonorifics(e.ref)}</p>
    </aside>
  );
}

/** "I saw the crescent": kept in Your nights, then the supplication (unless it is already on show beside it, `prayer`
 *  false). `month` is the month whose crescent it is. */
export function SawCrescent({ month, prayer = true }: { month: HijriMonth; prayer?: boolean }) {
  const all = nights.value;
  const seen = Object.values(all).some((n) => n.sightings.some((x) => x.key === crescentKey(month)));
  const [just, setJust] = useState(false);
  const s = services.value;
  return (
    <>
      {!seen && s && (
        <button class="btn saw-crescent" onClick={() => setJust(sawCrescent(s, month))}>
          <Icon name="moon" size={18} /> I saw the crescent
        </button>
      )}
      {prayer && (seen || just) && <CrescentPrayer />}
    </>
  );
}

/** Whether `t` is in the evening of the 29th at home, from `before` ms ahead of its sunset to `after` ms beyond it. */
function evening29(t: number, before: number, after: number) {
  const ev = eveningOf29(t, home(), saudiStarts.value);
  return ev && t >= ev.sunset - before && t <= ev.sunset + after ? ev : null;
}

/** Tonight, on the evening of the 29th: where to look, the supplication, and "I saw the crescent". */
export function CrescentGuide() {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 60e3);
    return () => clearInterval(id);
  }, []);
  if (!live()) return null;
  const now = Date.now();
  const ev = evening29(now, 4 * HOUR, 3 * HOUR);
  if (!ev) return null;
  const month = crescentMonthAt(now, home(), saudiStarts.value) ?? ev.month;
  return (
    <div class="card wide crescent-guide">
      <div class="card-title"><Icon name="moon" size={14} /> New crescent (hilāl) of {hijriName(ev.month)}</div>
      <div class="card-main">Look low in the west just after sunset.</div>
      <button class="btn-ghost small" onClick={() => { selection.value = { kind: 'body', id: 'Moon' }; panel.value = null; }}>
        <Icon name="eye" size={16} /> Show me
      </button>
      <CrescentPrayer />
      {now >= ev.sunset && <SawCrescent month={month} prayer={false} />}
    </div>
  );
}

/** Keeps `crescentFrame` set from half an hour before sunset on the 29th to an hour and a half after, at home, live. */
export function useCrescentFrame() {
  useEffect(() => {
    const check = () => {
      const on = timeLive.peek() && !previewFrom.peek() && !reliving.peek() && !!evening29(Date.now(), 0.5 * HOUR, 1.5 * HOUR);
      if (crescentFrame.peek() !== on) crescentFrame.value = on;
    };
    check();
    const id = setInterval(check, 30e3);
    const offs = [timeLive.subscribe(check), previewFrom.subscribe(check), reliving.subscribe(check), saudiStarts.subscribe(check)];
    return () => {
      clearInterval(id);
      offs.forEach((off) => off());
    };
  }, []);
}
